'use client';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { elowenClient } from "../elowenClient";
import { QUERY_KEYS } from "../queries";
import type { SkinCreateRequest } from "../types";

/** Add a customer skin. The daemon validates the manifest and the whole token sheet before it writes, and
 *  a refusal carries the reason the operator has to fix — which the form shows as it is.
 *
 *  The catalogue is invalidated with the admin listing: it is what the open document resolves against, so
 *  without this the skin just added is offered nowhere (the allow-list and the switcher both filter through
 *  it) until the next page load. The Settings rows keep their own copy of the same listing. */
export function useCreateSkin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draft: SkinCreateRequest) => elowenClient.createSkin(draft),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.skinAdmin });
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.skinCatalogue });
    },
  });
}

/** Remove a customer skin the store created. Refused — with the reason — for a folder holding anything
 *  but the text and generated font files of the contract: those files are not this API's to delete. The catalogue is
 *  invalidated too, so a document wearing the skin that just went away falls to the instance default
 *  instead of keeping a design nothing can paint. */
/** Font changes move the CSS digest and must update both the picker and the active document. */
export function useSkinFont() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; family: string; file: File } | { id: string; remove: true }) =>
      'remove' in input ? elowenClient.deleteSkinFont(input.id) : elowenClient.uploadSkinFont(input.id, input.family, input.file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.skinAdmin });
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.skinCatalogue });
    },
  });
}

export function useDeleteSkin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => elowenClient.deleteSkin(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.skinAdmin });
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.skinCatalogue });
    },
  });
}
